import { ReactComponent as AIProcessingIcon } from 'assets/images/ai/ai-processing.svg'
import { useEffect, useState } from 'react'
import type { Message } from 'slices/aiConversations/types'
import './ToolCallMessage.scss'

export interface ToolCallMessageProps {
  message: Message
}

// Global state to track tool call text transitions across component remounts
let globalToolCallState = {
  previousText: '',
  currentText: '',
  isTransitioning: false,
  transitionTimeout: null as number | null,
}

const ToolCallMessage = ({ message }: ToolCallMessageProps) => {
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [displayText, setDisplayText] = useState(message.content)

  useEffect(() => {
    // Clear any pending transition
    if (globalToolCallState.transitionTimeout) {
      clearTimeout(globalToolCallState.transitionTimeout)
    }

    // If content has changed from the global state, trigger transition
    if (globalToolCallState.currentText !== message.content) {
      globalToolCallState.previousText = globalToolCallState.currentText
      globalToolCallState.currentText = message.content
      globalToolCallState.isTransitioning = true

      setIsTransitioning(true)
      setDisplayText(globalToolCallState.previousText || message.content)

      // After fade-out completes, update text and fade back in
      globalToolCallState.transitionTimeout = window.setTimeout(() => {
        setDisplayText(message.content)
        setIsTransitioning(false)
        globalToolCallState.isTransitioning = false
        globalToolCallState.transitionTimeout = null
      }, 200) // Half of the 0.4s transition duration
    } else {
      // No transition needed, just display the current content
      setDisplayText(message.content)
      setIsTransitioning(false)
    }

    return () => {
      if (globalToolCallState.transitionTimeout) {
        clearTimeout(globalToolCallState.transitionTimeout)
        globalToolCallState.transitionTimeout = null
      }
    }
  }, [message.content])

  return (
    <div className="tool-call-message">
      <div className="tool-call-message__icon" aria-hidden="true">
        <AIProcessingIcon />
      </div>
      <span
        className={`tool-call-message__text ${
          isTransitioning ? 'tool-call-message__text--fading' : ''
        }`}
      >
        {displayText}
      </span>
    </div>
  )
}

export default ToolCallMessage
